/**
 * PRINCÍPIOS:
 * - Backend, banco e frontend trabalham exclusivamente com UTC
 * - NÃO fazer nenhuma conversão de timezone
 * - Manter a data exatamente como recebida, apenas formatar
 * - Hora zero ou preenchida: manter como está
 *
 * FUNÇÕES PRINCIPAIS:
 * - formatDate(): Formata data UTC sem conversão
 * - formatDateTime(): data/hora com relógio UTC (relatórios, payload “como veio”)
 * - formatDateTimeLocal() / formatTimeLocal() / formatDateLocalCalendar(): exibem o **mesmo instante ISO**
 *   no fuso do navegador — usar em chat/notificações onde o usuário espera horário local (ex.: WhatsApp).
 * - prepareDateForAPIZeroTime(): Converte data local para UTC 00:00:00.000Z
 * - toLocalDateForValidityArithmetic(): Date de calendário local a partir de dayjs/Date/string (evita deslocar dia com ISO Z)
 * - handleDatePickerChange(): Converte dayjs para Date
 */

// Função para verificar se uma string é uma data UTC
export const isUTCDate = (dateString: string): boolean => {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/.test(dateString);
};

// Função principal para formatar datas UTC (sem conversão, apenas formatação)
export const formatDate = (
  date: string | Date | undefined,
  format?: 'short' | 'full' | 'chart' | 'long'
): string => {
  if (!date) return '-';

  let dateObj: Date;

  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }

  if (isNaN(dateObj.getTime())) {
    if (typeof date === 'string') return date;
    return '-';
  }

  // Usar componentes UTC para formatação (sem conversão de timezone)
  const day = dateObj.getUTCDate();
  const month = dateObj.getUTCMonth();
  const year = dateObj.getUTCFullYear();

  if (format === 'short') {
    // Formato: "dd de mmm" (ex: "07 de ago")
    const monthNames = [
      'jan',
      'fev',
      'mar',
      'abr',
      'mai',
      'jun',
      'jul',
      'ago',
      'set',
      'out',
      'nov',
      'dez',
    ];
    return `${day.toString().padStart(2, '0')} de ${monthNames[month]}`;
  }

  if (format === 'chart') {
    // Formato: "01/set" (ex: "01/set", "27/ago")
    const monthNames = [
      'jan',
      'fev',
      'mar',
      'abr',
      'mai',
      'jun',
      'jul',
      'ago',
      'set',
      'out',
      'nov',
      'dez',
    ];
    return `${day.toString().padStart(2, '0')}/${monthNames[month]}`;
  }

  if (format === 'long') {
    // Formato: "06 de Junho" (ex: "06 de Junho", "27 de Agosto")
    const monthNames = [
      'Janeiro',
      'Fevereiro',
      'Março',
      'Abril',
      'Maio',
      'Junho',
      'Julho',
      'Agosto',
      'Setembro',
      'Outubro',
      'Novembro',
      'Dezembro',
    ];
    return `${day.toString().padStart(2, '0')} de ${monthNames[month]}`;
  }

  // Formato padrão: "dd/mm/yyyy"
  return `${day.toString().padStart(2, '0')}/${(month + 1).toString().padStart(2, '0')}/${year}`;
};

// Função para formatar data e hora UTC (sem conversão de timezone)
export const formatDateTime = (dateString: string): string => {
  if (!dateString) return '';

  const dateObj = new Date(dateString);

  if (isNaN(dateObj.getTime())) {
    return dateString;
  }

  // Formato: "dd/mm/yyyy hh:mm" usando componentes UTC
  const day = dateObj.getUTCDate().toString().padStart(2, '0');
  const month = (dateObj.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getUTCFullYear();
  const hours = dateObj.getUTCHours().toString().padStart(2, '0');
  const minutes = dateObj.getUTCMinutes().toString().padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/**
 * Data/hora no fuso local do navegador (mesmo instante ISO, exibição “relógio de parede” local).
 * Uso típico: chat WhatsApp, notificações. Para espelhar o relógio UTC do payload, use `formatDateTime`.
 */
export const formatDateTimeLocal = (dateString: string): string => {
  if (!dateString) return '';

  const dateObj = new Date(dateString);

  if (isNaN(dateObj.getTime())) {
    return dateString;
  }

  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

/** Somente hh:mm no horário local (bolhas de mensagem). */
export const formatTimeLocal = (dateString: string): string => {
  if (!dateString) return '';

  const dateObj = new Date(dateString);

  if (isNaN(dateObj.getTime())) {
    return '';
  }

  return `${dateObj.getHours().toString().padStart(2, '0')}:${dateObj.getMinutes().toString().padStart(2, '0')}`;
};

/** dd/mm/yyyy no calendário local (lista de conversas quando não é “hoje”). */
export const formatDateLocalCalendar = (date: string | Date | undefined): string => {
  if (!date) return '-';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    if (typeof date === 'string') return date;
    return '-';
  }

  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();

  return `${day}/${month}/${year}`;
};

// Função para preparar data para envio à API (mantém UTC)
export const prepareDateForAPI = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toISOString();
};

// Função para formatar data para exibição (mantém UTC)
export const formatDateForDisplay = (utcDate: string | Date): string => {
  return formatDate(utcDate);
};

// Função para enviar datas ao backend (converte para UTC com hora 00:00:00)
// Segue o guia: "Enviar no formato ISO 8601 UTC (YYYY-MM-DDTHH:mm:ss.sssZ)"
export const prepareDateForAPIZeroTime = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // Criar data UTC pura com hora 00:00:00.000Z (conforme guia)
  const utcDate = new Date(
    Date.UTC(
      dateObj.getFullYear(),
      dateObj.getMonth(),
      dateObj.getDate(),
      0,
      0,
      0,
      0 // Sempre hora 00:00:00.000Z
    )
  );

  return utcDate.toISOString();
};

// Função para validar qualquer tipo de data (dayjs, Date, string)
export const isValidDate = (date: any): boolean => {
  if (!date) return false;

  // Se for objeto dayjs, usar método isValid
  if (date && typeof date.isValid === 'function') {
    return date.isValid();
  }

  // Se for objeto Date, verificar se é válido
  if (date instanceof Date) {
    return !isNaN(date.getTime());
  }

  // Se for string, tentar converter para Date
  if (typeof date === 'string') {
    const dateObj = new Date(date);
    return !isNaN(dateObj.getTime());
  }

  // Para qualquer outro tipo, tentar converter para Date
  try {
    const dateObj = new Date(date);
    return !isNaN(dateObj.getTime());
  } catch {
    return false;
  }
};

/**
 * Normaliza valor do DatePicker ou string da API (dayjs, Date, ISO ou prefixo YYYY-MM-DD)
 * para um `Date` de calendário local (meia-noite local nesse dia).
 *
 * Use antes de aritmética com `getDate`/`setDate` ou ao recalcular fim de período no submit,
 * para não fazer `new Date("...T00:00:00.000Z")` (em fusos atrás de UTC o dia civil recua).
 */
export const toLocalDateForValidityArithmetic = (raw: unknown): Date | null => {
  if (!raw) return null;

  if (raw instanceof Date) {
    if (!isValidDate(raw)) return null;
    return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  }

  if (
    typeof raw === 'object' &&
    raw !== null &&
    typeof (raw as { toDate?: () => Date }).toDate === 'function'
  ) {
    const d = (raw as { toDate: () => Date }).toDate();
    if (!isValidDate(d)) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  if (typeof raw === 'string') {
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const y = Number(m[1]);
    const monthIndex = Number(m[2]) - 1;
    const day = Number(m[3]);
    const d = new Date(y, monthIndex, day);
    return isValidDate(d) ? d : null;
  }

  return null;
};

// Função específica para converter datas UTC para DatePicker sem conversão de timezone
export const convertUTCDateForDatePicker = (utcDateString: string | Date | any | null): any => {
  if (!utcDateString) return null;

  // Importar dayjs dinamicamente
  const dayjs = require('dayjs');

  // Se já é um objeto dayjs, retornar diretamente
  if (utcDateString && typeof utcDateString === 'object' && utcDateString.$L) {
    return utcDateString;
  }

  // Se é um objeto Date, extrair componentes UTC para evitar conversão de timezone
  if (utcDateString instanceof Date) {
    const year = utcDateString.getUTCFullYear();
    const month = utcDateString.getUTCMonth();
    const day = utcDateString.getUTCDate();

    // Criar dayjs apenas com a data usando componentes UTC
    const converted = dayjs(
      `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    );

    return converted;
  }

  // Se é uma string, extrair apenas a parte da data (YYYY-MM-DD) para evitar conversão de timezone
  if (typeof utcDateString === 'string') {
    const dateOnly = utcDateString.split('T')[0]; // Extrair apenas "2025-10-09"

    // Criar dayjs apenas com a data, sem hora/timezone
    const converted = dayjs(dateOnly);

    return converted;
  }

  // Fallback: tentar converter diretamente
  return dayjs(utcDateString);
};

// Função para validar e converter date do DatePicker
export const handleDatePickerChange = (date: any): Date | null => {
  return toLocalDateForValidityArithmetic(date);
};

/** Converte data máxima da API (YYYY-MM-DD ou ISO) para Date de calendário local. */
export const parseApiMaxDateToLocalDate = (raw: unknown): Date | null => {
  return toLocalDateForValidityArithmetic(raw);
};

// Função para extrair apenas a data no formato YYYY-MM-DD de dayjs, Date ou string
// Útil para parâmetros de API que esperam apenas a data sem hora
export const getDateOnlyString = (date: any): string | undefined => {
  if (!date) return undefined;

  // Se for objeto dayjs
  if (date && typeof date.isValid === 'function' && date.isValid()) {
    return date.format('YYYY-MM-DD');
  }

  // Se for string e já está no formato YYYY-MM-DD, retornar diretamente
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  // Para Date ou string ISO, converter para Date e extrair componentes UTC
  let dateObj: Date;
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string') {
    dateObj = new Date(date);
  } else {
    return undefined;
  }

  if (isNaN(dateObj.getTime())) {
    return undefined;
  }

  // Extrair componentes UTC
  const year = dateObj.getUTCFullYear();
  const month = (dateObj.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = dateObj.getUTCDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};
